import React, { useState, memo } from 'react';
import { useVote } from '@/hooks/useVote';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ChevronUp } from 'lucide-react';

const sizeClasses = {
  sm: {
    container: 'p-1',
    icon: 'h-4 w-4',
    score: 'text-sm',
  },
  md: {
    container: 'p-2',
    icon: 'h-6 w-6',
    score: 'text-lg',
  },
  lg: {
    container: 'p-3',
    icon: 'h-8 w-8',
    score: 'text-2xl',
  },
} as const;

interface VoteButtonProps {
  postId: string;
  initialScore: number;
  initialVoted?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  hideIcon?: boolean;
}

export const VoteButton = memo(function VoteButton({
  postId,
  initialScore,
  initialVoted = false,
  size = 'md',
  onClick,
  disabled = false,
  hideIcon = false,
}: VoteButtonProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { voted, score, toggleVote, isLoading } = useVote({
    postId,
    initialVoted,
    initialScore,
  });
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (onClick) {
      onClick(e);
    }

    // Don't allow voting if disabled
    if (disabled) {
      return;
    }

    // Require authentication
    if (!isAuthenticated) {
      navigate('/auth/login');
      return;
    }

    // Trigger animation
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);

    toggleVote();
  };

  const classes = sizeClasses[size];

  const ariaLabel = disabled
    ? 'Cannot vote on your own post'
    : voted
      ? `Remove vote. Current votes: ${score}`
      : `Upvote. Current votes: ${score}`;

  return (
    <button
      onClick={handleClick}
      disabled={isLoading || disabled}
      className={cn(
        'flex flex-col items-center rounded-lg transition-all duration-200',
        classes.container,
        voted
          ? 'bg-primary/10 hover:bg-primary/20'
          : 'hover:bg-muted',
        (isLoading || disabled) && 'opacity-50 cursor-not-allowed',
        disabled && 'pointer-events-none',
        isAnimating && 'scale-110'
      )}
      title={disabled ? 'Cannot vote on your own post' : (voted ? 'Remove vote' : 'Upvote')}
      aria-label={ariaLabel}
      aria-pressed={voted}
    >
      {!hideIcon && (
        <ChevronUp
          className={cn(
            classes.icon,
            'transition-all duration-200',
            voted ? 'text-primary' : 'text-muted-foreground',
            !isLoading && !disabled && 'group-hover:text-primary',
            isAnimating && voted && '-translate-y-0.5'
          )}
          fill={voted ? 'currentColor' : 'none'}
          strokeWidth={voted ? 2.5 : 2}
          aria-hidden="true"
        />
      )}
      <span
        className={cn(
          'font-semibold mt-0.5 transition-all duration-200',
          classes.score,
          voted ? 'text-primary' : 'text-foreground',
          isAnimating && 'scale-110'
        )}
      >
        {score}
      </span>
    </button>
  );
});
